import { Dispatch, SetStateAction, useState } from 'react'

import { toastContainerSchema } from '@/@types/schemas/toast-containers'
import { showError, showSuccess } from '@/components/toast'
import { useCompany } from '@/contexts/company'
import useTranslator from '@/hooks/translation'
import { useUploadFiles } from '@/hooks/upload/files'
import { useRouteCompanyId } from '@/hooks/use-route-company-id'
import { createProject } from '@/services/fintalk/services/fintalk-backoffice/projects/create'

import {
  ProjectsCreateFields,
  ProjectsCreateSchemaMethods,
  ProjectsCreateSteps,
  projectsCreateSteps,
} from './types'

export const useProjectsCreate = (
  isModalOpen?: boolean,
  setIsModalOpen?: (isModalOpen: boolean) => void,
) => {
  const companyId = useRouteCompanyId()

  const { loadProjects } = useCompany()

  const { getTranslation } = useTranslator()

  const { uploadFile } = useUploadFiles()

  const [projectFileS3Key, setProjectFileS3Key] = useState<string | null>(null)

  const formStepsToFieldsMap: {
    [Num in ProjectsCreateSteps]: Array<ProjectsCreateFields>
  } = {
    1: ['picture', 'name'],
    2: ['botIds'],
  }

  const formTitleStep: {
    [Num in ProjectsCreateSteps]: string
  } = {
    1: getTranslation('common.next'),
    2: getTranslation('common.newProject'),
  }

  const getStepTitle = (currentStep: ProjectsCreateSteps) => {
    return formTitleStep[currentStep]
  }

  const setInitialProjectPicture = (key: string) => {
    setProjectFileS3Key(key)
  }

  const handleChangeFormStep = async (
    changeFormStep: 1 | -1,
    setIsLoadingStep: Dispatch<SetStateAction<boolean>>,
    formMethods: ProjectsCreateSchemaMethods,
  ) => {
    const { trigger, getValues, setValue } = formMethods

    const { currentStep, picture, name, botIds } = getValues()

    if (!formStepsToFieldsMap[currentStep]) return

    let newStep = (currentStep + changeFormStep) as ProjectsCreateSteps

    const isValidStep = projectsCreateSteps.safeParse(newStep)

    if (!isValidStep.success) {
      newStep = newStep > 2 ? 2 : 1
    }

    const shouldValidateFields = changeFormStep > 0

    if (
      shouldValidateFields &&
      !(await trigger(formStepsToFieldsMap[currentStep]))
    ) {
      return
    }

    if (shouldValidateFields) {
      setIsLoadingStep(true)

      try {
        if (currentStep === 1) {
          if (!companyId) return

          let key = null

          if (picture instanceof File) {
            key = await uploadFile(picture, 'project')
          }

          if (picture === '') {
            key = ''
          }

          setProjectFileS3Key(key)
        }

        if (currentStep === 2) {
          if (!companyId) return

          try {
            await createProject({
              companyId,
              name,
              bots: botIds,
              fileS3Key: projectFileS3Key || undefined,
            })
          } catch {
            showError({
              title: getTranslation('projects.toast.error.created.title'),
              description: getTranslation(
                'projects.toast.error.created.description',
              ),
              containerId: toastContainerSchema.Enum.PROJECTS,
            })

            return
          }

          showSuccess({
            title: getTranslation('projects.toast.success.created.title'),
            description: getTranslation(
              'projects.toast.success.created.description',
            ),
            containerId: toastContainerSchema.Enum.PROJECTS,
          })

          if (setIsModalOpen) {
            setIsModalOpen(false)
          }

          await loadProjects()
        }

        setValue('currentStep', newStep || currentStep, {
          shouldValidate: true,
        })
      } catch {
        showError({
          title: getTranslation('common.toast.error.error.title'),
          description: getTranslation('common.toast.error.error.description'),
          containerId: toastContainerSchema.Enum.PROJECTS,
        })
      } finally {
        setIsLoadingStep(false)
      }
    } else {
      setValue('currentStep', newStep || currentStep, {
        shouldValidate: true,
      })
    }
  }

  return {
    handleChangeFormStep,
    getStepTitle,
    isModalOpen,
    setIsModalOpen,
    setInitialProjectPicture,
  }
}
